import { faTriangleExclamation } from "@fortawesome/pro-duotone-svg-icons"
import { faPen } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { CircularProgress, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { NOT_SAVED_ID } from "app/model"
import { getFormatedNumber } from "app/tools/number"
import { CURRENCIES } from "currency/model"
import { getCurrencySymbol } from "form/inventory/tools/common"
import { IForm, IFormConfigurationBudget, IFormConfigurationMain } from "form/model"
import {
  isCampaignBudgetGreaterThanOrderBudget,
  isCampaignImpressionsGreaterThanOrderImpression
} from "form/tools/configuration"
import { orderEventModule } from "order/event"
import { IOrder } from "order/model"
import { getOrderById } from "order/tools/common"
import { openEditOrderPopup } from "order/tools/form"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"

interface IOrderEditFieldProps {
  form: IForm
  toDisplayInfo: "budget" | "impressions"
  budgetForm?: IFormConfigurationBudget // display a warning if order budget is lower
  mainForm?: IFormConfigurationMain // display a warning if order impression is lower
  sx?: any
}

export const OrderEditField = React.memo(
  ({ form, toDisplayInfo, budgetForm, mainForm, sx }: IOrderEditFieldProps): JSX.Element => {
    const orderId = form.configuration.main.order_id
    const [order, setOrder] = React.useState<IOrder | null>(null)
    const [hasLoadingError, setHasLoadingError] = React.useState(false)
    const toDisplayValue: number | null | undefined = !order
      ? null
      : toDisplayInfo === "budget"
        ? order.budget
        : order.max_impressions
    const eventName = `component-order-edit-field-${toDisplayInfo}`
    const onComponentUmount = () => orderEventModule.remove(eventName)
    React.useEffect(() => {
      orderEventModule.on.onSave(eventName, () => setOrder(null))
      if (typeof orderId !== "number" || orderId === NOT_SAVED_ID || hasLoadingError) {
        return onComponentUmount
      }
      if (order && order.id === orderId) {
        return onComponentUmount
      }
      if (order) {
        setOrder(null)
        setHasLoadingError(false)
      }
      getOrderById(orderId)
        .then(fetchedOrder => setOrder(fetchedOrder))
        .catch(error => {
          console.error(error)
          setHasLoadingError(true)
        })
      return onComponentUmount
    }, [orderId, order])
    const warningMessage = !order
      ? undefined
      : toDisplayInfo === "budget"
        ? budgetForm
          ? isCampaignBudgetGreaterThanOrderBudget(budgetForm.budget, budgetForm.currency_id, order)
            ? translate("campaign.budget.greater.order")
            : undefined
          : undefined
        : mainForm
          ? mainForm.unlimitedImpressions
            ? undefined
            : isCampaignImpressionsGreaterThanOrderImpression(mainForm.max_views, order)
              ? translate("campaign.impression.greater.order")
              : undefined
          : undefined
    return (
      <FlexBox vertical ai="start" jc="start" gap=".2rem" fullWidth sx={{ mb: ".5rem", ...sx }}>
        <Typography id={`order-${toDisplayInfo}-title`} color="textSecondary" fontSize=".8rem">
          {translate(`order.${toDisplayInfo}`)}
        </Typography>
        {hasLoadingError ? (
          <Typography id={`order-${toDisplayInfo}-error`} color="error" fontSize=".75rem">
            {translate("order.field.error")}
          </Typography>
        ) : typeof orderId !== "number" || orderId === NOT_SAVED_ID ? (
          <Typography id={`order-${toDisplayInfo}-empty`} color="textPrimary" fontSize=".75rem">
            {translate("order.not.selected")}
          </Typography>
        ) : !order ? (
          <CircularProgress id={`order-${toDisplayInfo}-loading`} />
        ) : (
          <>
            <FlexBox
              sx={{
                "& .icon": {
                  "cursor": "pointer",
                  "ml": ".5rem",
                  "color": theme => theme.palette.text.primary2,
                  "&:hover": { color: theme => theme.palette.primary.main }
                }
              }}>
              <Typography
                id={`order-${toDisplayInfo}-value`}
                color={!toDisplayValue ? "textSecondary" : "textPrimary"}
                fontSize=".75rem">
                {!toDisplayValue ? translate("label.none") : getFormatedNumber(toDisplayValue)}
                {toDisplayInfo === "budget" ? ` ${getCurrencySymbol(order.budget_currency_id || CURRENCIES.CAD)}` : ""}
              </Typography>
              <FontAwesomeIcon
                id={`order-${toDisplayInfo}-edit-button`}
                icon={faPen}
                onClick={() => openEditOrderPopup(order.id)}
                className="icon"
              />
            </FlexBox>
            {warningMessage && (
              <FlexBox gap sx={{ "& .icon": { color: theme => theme.palette.warning.main } }}>
                <FontAwesomeIcon size="2x" icon={faTriangleExclamation} className="icon" />
                <Typography id={`order-${toDisplayInfo}-warning`} color="textPrimary" fontSize=".8rem" fontWeight="500">
                  {warningMessage}
                </Typography>
              </FlexBox>
            )}
          </>
        )}
      </FlexBox>
    )
  }
)
